import { FileUnknownOutlined } from '@ant-design/icons';
import { ProCard, ProTable } from '@ant-design/pro-components';
import { useQuery } from '@tanstack/react-query';
import { useSetState } from 'ahooks';
import {
    Avatar,
    Button,
    Checkbox,
    DatePicker,
    Flex,
    Form,
    Image,
    Input,
    Popconfirm,
    Select,
    Space,
    Spin,
    Typography,
} from 'antd';
import { isEqual, mapValues } from 'lodash';
import prettyBytes from 'pretty-bytes';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * APIs
 */
import { attachmentOptions, attachments, dAttachment, uAttachment } from '@/apis/attachment';

/**
 * Components
 */
import ComponentCreateUpdate from './create_update';

/**
 * Types
 */
import type { AttachmentWithUserDto, QueryAttachmentDto } from '@/dtos/attachment.dto';
import type { ActionType, ProTableProps } from '@ant-design/pro-components';
import type { FormProps, PaginationProps } from 'antd';
import type { Action } from './create_update';

/**
 * Constants
 */
import { RANGE_PICKER_PRESETS } from '@/constants/presets';

const defaultPayload: QueryAttachmentDto = {
    app: undefined,
    module: undefined,
    type: undefined,
    is_deleted: false,
    is_violation: false,
};

const PageAttachments: React.FC = () => {
    /**
     * Hooks
     */
    const [formSearch] = Form.useForm();

    const tableRef = useRef<ActionType>();

    const updateRef = useRef<Action>();

    const { t } = useTranslation();

    /**
     * States
     */
    const [loading, setLoading] = useSetState<Record<string, boolean>>({});

    /**
     * Paginations
     */
    const [pagination, setPagination] = useSetState<PaginationProps>({
        current: 1,
        pageSize: 20,
        showQuickJumper: true,
        showSizeChanger: true,
        onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
    });

    /**
     * Payloads
     */
    const [payload, setPayload] = useSetState<QueryAttachmentDto>({
        ...defaultPayload,
    });

    /**
     * Requests
     */
    const {
        data: dataAttachments,
        isLoading: loadingAttachments,
        refetch: refetchAttachments,
    } = useQuery({
        queryKey: ['attachments', pagination.current, pagination.pageSize, payload],
        queryFn: () => attachments({ page: pagination.current, size: pagination.pageSize, ...payload }),
        retry: false,
    });

    const { data: dataAttachmentOptions, isLoading: loadingAttachmentOptions } = useQuery({
        queryKey: ['attachment-options'],
        queryFn: () => attachmentOptions(),
        retry: false,
    });

    /**
     * ChildrenProps
     */
    const formSearchProps: FormProps = {
        form: formSearch,
        initialValues: {
            ...defaultPayload,
        },
        layout: 'inline',
        onFinish: (values) => {
            values.created_at = [
                values.created_at?.[0].startOf('day').format(),
                values.created_at?.[1].endOf('day').format(),
            ];

            const assignPayload = {
                ...defaultPayload,
                ...mapValues(values, (value) => (value === '' ? undefined : value)),
            };

            if (isEqual(payload, assignPayload)) {
                return refetchAttachments();
            }

            setPagination({
                current: 1,
            });

            setPayload(assignPayload);
        },
    };

    const proTableProps: ProTableProps<AttachmentWithUserDto, QueryAttachmentDto> = {
        actionRef: tableRef,
        bordered: true,
        columns: [
            {
                align: 'center',
                dataIndex: 'path',
                title: t('preview'),
                width: 100,
                render: (_, entity) => {
                    if (entity.type === 'image') {
                        return (
                            <Image
                                alt=""
                                height={80}
                                preview={{
                                    src: `${import.meta.env.VITE_CDN}/cdn-cgi/image/format=auto,width=1200/${entity.path}`,
                                }}
                                src={`${import.meta.env.VITE_CDN}/cdn-cgi/image/fit=cover,format=auto,gravity=center,height=160,width=160/${entity.path}`}
                                width={80}
                            />
                        );
                    }

                    return <FileUnknownOutlined style={{ fontSize: 80 }} />;
                },
            },
            {
                dataIndex: 'name',
                ellipsis: true,
                title: t('name'),
            },
            {
                dataIndex: 'app',
                title: t('page.attachments.appAndModule'),
                width: 200,
                renderText: (_, record) => `${record.app} / ${record.module}`,
            },
            {
                dataIndex: 'specs',
                title: t('page.attachments.specs'),
                width: 200,
                renderText: (_, record) => `${record.extension.toUpperCase()} / ${prettyBytes(record.size)}`,
            },
            {
                dataIndex: 'type',
                title: t('page.attachments.type'),
                width: 100,
                renderText: (_, record) => t(`page.attachments.${record.type}`),
            },
            {
                dataIndex: 'user_id',
                title: t('user'),
                width: 300,
                render: (_, entity) =>
                    entity.user ? (
                        <Space align="center">
                            <Avatar
                                alt=""
                                src={entity.user.avatar && `${import.meta.env.VITE_CDN}/${entity.user.avatar}`}
                            >
                                {entity.user.nickname?.charAt(0).toUpperCase()}
                            </Avatar>

                            <Typography.Text copyable={{ text: entity.user_id! }}>
                                {entity.user.nickname} (@{entity.user.username})
                            </Typography.Text>
                        </Space>
                    ) : (
                        '-'
                    ),
            },
            {
                dataIndex: 'created_at',
                title: t('created_at'),
                valueType: 'dateTime',
                width: 200,
            },
            {
                fixed: 'right',
                key: 'option',
                title: t('actions'),
                valueType: 'option',
                width: 200,
                render: (_, entity) => [
                    <a
                        key="update"
                        onClick={() => {
                            updateRef.current?.update(entity);
                        }}
                    >
                        {t('edit')}
                    </a>,
                    <Spin key="delete" size="small" spinning={loading[entity.id] === true}>
                        <Popconfirm
                            title={t('ays')}
                            onConfirm={() => {
                                setLoading({ [entity.id]: true });
                                (entity.is_deleted
                                    ? uAttachment({ id: entity.id, is_deleted: false, version: entity.version })
                                    : dAttachment(entity.id)
                                )
                                    .then(() => refetchAttachments())
                                    .finally(() => {
                                        setLoading({
                                            [entity.id]: false,
                                        });
                                    });
                            }}
                        >
                            {entity.is_deleted ? (
                                <Typography.Link type="success">{t('restore')}</Typography.Link>
                            ) : (
                                <Typography.Link type="danger">{t('delete')}</Typography.Link>
                            )}
                        </Popconfirm>
                    </Spin>,
                ],
            },
        ],
        dataSource: dataAttachments?.data ?? [],
        loading: loadingAttachments,
        options: {
            reload: () => refetchAttachments(),
        },
        pagination: {
            ...pagination,
            total: dataAttachments?.meta.total || 0,
        },
        rowKey: 'id',
        scroll: {
            x: 1500,
        },
        search: false,
        toolbar: {
            actions: [
                <ComponentCreateUpdate key="create_update" ref={updateRef} onFinish={() => refetchAttachments()} />,
            ],
        },
    };

    return (
        <>
            <ProCard className="mb-16">
                <Form {...formSearchProps}>
                    <Flex gap={16} wrap="wrap">
                        <Form.Item name="app" style={{ marginRight: 0 }}>
                            <Select
                                allowClear={true}
                                loading={loadingAttachmentOptions}
                                options={(dataAttachmentOptions?.data.apps ?? []).sort().map((app) => ({
                                    label: app,
                                    value: app,
                                }))}
                                placeholder={t('page.attachments.app')}
                                style={{ width: 200 }}
                            />
                        </Form.Item>

                        <Form.Item name="module" style={{ marginRight: 0 }}>
                            <Select
                                allowClear={true}
                                loading={loadingAttachmentOptions}
                                options={(dataAttachmentOptions?.data.modules ?? []).sort().map((module) => ({
                                    label: module,
                                    value: module,
                                }))}
                                placeholder={t('page.attachments.module')}
                                style={{ width: 200 }}
                            />
                        </Form.Item>

                        <Form.Item name="type" style={{ marginRight: 0 }}>
                            <Select
                                allowClear={true}
                                loading={loadingAttachmentOptions}
                                options={(dataAttachmentOptions?.data.types ?? []).sort().map((type) => ({
                                    label: t(`page.attachments.${type}`),
                                    value: type,
                                }))}
                                placeholder={t('page.attachments.type')}
                                style={{ width: 200 }}
                            />
                        </Form.Item>

                        <Form.Item name="name_fuzzy" style={{ marginRight: 0 }}>
                            <Input allowClear={true} placeholder={t('name')} style={{ width: 200 }} />
                        </Form.Item>

                        <Form.Item name="user_id" style={{ marginRight: 0 }}>
                            <Input allowClear={true} placeholder={t('user_id')} style={{ width: 200 }} />
                        </Form.Item>

                        <Form.Item label={t('created_at')} name="created_at" style={{ marginRight: 0 }}>
                            <DatePicker.RangePicker allowClear={true} presets={RANGE_PICKER_PRESETS(t)} />
                        </Form.Item>

                        <Form.Item name="is_deleted" style={{ marginRight: 0 }} valuePropName="checked">
                            <Checkbox>{t('status_deleted')}</Checkbox>
                        </Form.Item>

                        <Form.Item name="is_violation" style={{ marginRight: 0 }} valuePropName="checked">
                            <Checkbox>{t('status_violation')}</Checkbox>
                        </Form.Item>

                        <Button
                            loading={loadingAttachments}
                            type="primary"
                            onClick={() => {
                                formSearch.submit();
                            }}
                        >
                            {t('search')}
                        </Button>

                        <Button
                            onClick={() => {
                                formSearch.resetFields();
                            }}
                        >
                            {t('reset')}
                        </Button>
                    </Flex>
                </Form>
            </ProCard>

            <ProTable {...proTableProps} />
        </>
    );
};

export default PageAttachments;
